import Box from '@mui/material/Box';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { PopoverOrigin, PopoverPosition } from '@mui/material/Popover';
import { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uid } from 'uuid';

const ITEM_HEIGHT = 48;

interface OptionsProps {
  callBack: () => void;
  disabled?: boolean;
  icon?: React.ReactElement;
  title: string;
}

interface SimpleMenuProps {
  anchorOrigin?: PopoverOrigin;
  anchorPosition?: PopoverPosition;
  children?: React.ReactNode | undefined;
  disabled?: boolean;
  keepMounted?: boolean;
  menuTitle?: string;
  options: Array<OptionsProps>;
  size?: IconButtonProps['size'];
  sx?: SxProps<Theme>;
  transformOrigin?: PopoverOrigin;
}

interface SimpleMenuHandle {
  toggle: () => void;
}

const SimpleMenu = forwardRef<SimpleMenuHandle, SimpleMenuProps>(function SimpleMenu(
  {
    children,
    options,
    sx,
    anchorPosition,
    keepMounted,
    transformOrigin,
    anchorOrigin,
    disabled,
    menuTitle,
    size,
  },
  ref,
) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hasClickedMenu, setHasClickedMenu] = useState(false);
  const open = Boolean(anchorEl);
  const iconButtonRef = useRef<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setHasClickedMenu(false);
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const rnd = useMemo(() => uid(), []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useImperativeHandle(ref, () => ({
    toggle() {
      iconButtonRef.current?.click();
    },
  }));

  if (!options.length) {
    return null;
  }

  return (
    <Box data-test-id="simple-menu">
      <IconButton
        ref={iconButtonRef}
        aria-label="more"
        id={`menu-button_${rnd}`}
        aria-controls={open ? `menu_${rnd}` : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        disabled={disabled}
        size={size}
      >
        {children || 'Menu'}
      </IconButton>
      <Menu
        transformOrigin={transformOrigin}
        id={`menu_${rnd}`}
        anchorOrigin={anchorOrigin}
        anchorEl={anchorEl}
        anchorPosition={anchorPosition}
        keepMounted={keepMounted}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: { 'aria-labelledby': `menu-button_${rnd}` },
          paper: {
            sx,
            style: { maxHeight: ITEM_HEIGHT * 4.5, minWidth: '20ch' },
          },
        }}
      >
        {menuTitle && (
          <Typography variant="body1" sx={{ pl: 2.5, pt: 0.5, pb: 0.75 }}>
            {menuTitle}
          </Typography>
        )}
        {options.map((option, index) => (
          <Tooltip
            title={option.title}
            enterDelay={600}
            leaveDelay={50}
            key={`menu-item-${option.title}-${index}`}
          >
            <MenuItem
              // Prevent clicks to other menu items after one is clicked
              disabled={option.disabled || hasClickedMenu}
              onClick={() => {
                // Prevent double click on menu item
                if (hasClickedMenu) {
                  return;
                }
                setHasClickedMenu(true);
                option.callBack.apply(null, []);
                handleClose();
              }}
            >
              {option.icon ? <ListItemIcon>{option.icon}</ListItemIcon> : null}
              {option.title}
            </MenuItem>
          </Tooltip>
        ))}
      </Menu>
    </Box>
  );
});

export { SimpleMenu };
